import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';

import { Users, FileText, Plus, Search, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import BackButton from '../components/BackButton';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function CoopersAdhesions() {
  const { getAuthHeader } = useAuth();
  
  // States
  const [membresAdherents, setClients] = useState([]);
  const [souscriptions, setSouscriptions] = useState([]);
  const [dispositifs, setDispositifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Filtres
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Formulaire nouvelle souscription
  const [formData, setFormData] = useState({
    member_id: '',
    dispositif_id: '',
    options_choisies: [],
    nombre_jours: null
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, souscriptionsRes, dispositifsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/members/coopers/membresAdherents`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/souscriptions/coopers/membresAdherents`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/dispositifs`)
      ]);
      
      // Trier du plus récent au plus ancien
      const sortByDate = (arr) => (arr || []).sort((a, b) => new Date(b.date_creation || b.created_at || 0) - new Date(a.date_creation || a.created_at || 0));
      setClients(sortByDate(clientsRes.data.members || []));
      setSouscriptions(sortByDate(souscriptionsRes.data.souscriptions || []));
      setDispositifs(sortByDate(dispositifsRes.data || []));
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSouscription = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/souscriptions/coopers/create-for-membreAdherent`,
        formData,
        { headers: getAuthHeader() }
      );
      
      alert('Souscription créée avec succès !');
      setDialogOpen(false);
      setFormData({
        member_id: '',
        dispositif_id: '',
        options_choisies: [],
        nombre_jours: null
      });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la création de la souscription');
    }
  };

  const getStatusBadge = (souscription) => {
    const dateFin = new Date(souscription.date_fin);
    const now = new Date();
    
    if (!souscription.is_active) {
      return <Badge variant="destructive">Inactive</Badge>;
    } else if (dateFin < now) {
      return <Badge variant="destructive">Expirée</Badge>;
    } else if ((dateFin - now) / (1000 * 60 * 60 * 24) <= 30) {
      return <Badge className="bg-orange-700 text-white font-semibold">Expire bientôt</Badge>;
    } else {
      return <Badge className="bg-green-700 text-white font-semibold">Active</Badge>;
    }
  };

  const filteredSouscriptions = souscriptions.filter(sub => {
    const matchSearch = searchQuery === '' || 
      sub.numero_police.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sub.member?.nom.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sub.member?.prenom.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (filterStatus === 'all') return matchSearch;
    
    const dateFin = new Date(sub.date_fin);
    const now = new Date();
    
    if (filterStatus === 'active' && sub.is_active && dateFin >= now) return matchSearch;
    if (filterStatus === 'expired' && (dateFin < now || !sub.is_active)) return matchSearch;
    if (filterStatus === 'expiring' && sub.is_active && (dateFin - now) / (1000 * 60 * 60 * 24) <= 30) return matchSearch;
    
    return false;
  });

  const stats = {
    totalClients: membresAdherents.length,
    totalSouscriptions: souscriptions.length,
    souscriptionsActives: souscriptions.filter(s => s.is_active && new Date(s.date_fin) >= new Date()).length,
    souscriptionsExpirees: souscriptions.filter(s => !s.is_active || new Date(s.date_fin) < new Date()).length
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="text-lg text-gray-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Bouton Retour */}
      <div className="mb-4">
        <BackButton label="Retour" />
      </div>
      
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Gestion des Adhésions</h1>
          <p className="text-gray-600">Gérez les souscriptions de vos membres adhérents aux dispositifs solidaires</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle Souscription
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Créer une Souscription pour un membre adhérent</DialogTitle>
              <DialogDescription>
                Sélectionnez un membre adhérent et un dispositif solidaire
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateSouscription} className="space-y-4">
              <div>
                <Label htmlFor="member_id">Membre adhérent *</Label>
                <Select
                  value={formData.member_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, member_id: value }))}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionnez un membre adhérent" />
                  </SelectTrigger>
                  <SelectContent>
                    {membresAdherents.filter(m => m.id && m.id.trim() !== '').map(membreAdherent => (
                      <SelectItem key={membreAdherent.id} value={membreAdherent.id}>
                        {membreAdherent.nom} {membreAdherent.prenom} - {membreAdherent.entreprise || 'Particulier'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="dispositif_id">Dispositif Solidaire *</Label>
                <Select
                  value={formData.dispositif_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, dispositif_id: value }))}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionnez un dispositif" />
                  </SelectTrigger>
                  <SelectContent>
                    {dispositifs.filter(d => d.id && d.id.trim() !== '').map(disp => (
                      <SelectItem key={disp.id} value={disp.id}>
                        {disp.nom} - {disp.tarif_base}€
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="nombre_jours">Nombre de jours supplémentaires (optionnel)</Label>
                <Input
                  id="nombre_jours"
                  type="number"
                  min="0"
                  value={formData.nombre_jours || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, nombre_jours: e.target.value ? parseInt(e.target.value) : null }))}
                />
              </div>

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit">
                  Créer la Souscription
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              membresAdherents
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.totalClients}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Total Souscriptions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.totalSouscriptions}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              Actives
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{stats.souscriptionsActives}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <XCircle className="h-4 w-4 text-red-500" />
              Expirées
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600">{stats.souscriptionsExpirees}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filtres */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="search">Rechercher</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="search"
                  placeholder="Réf.Solidaire, nom, prénom..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="filterStatus">Statut</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  <SelectItem value="active">Actives</SelectItem>
                  <SelectItem value="expiring">Expire bientôt</SelectItem>
                  <SelectItem value="expired">Expirées</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            {filteredSouscriptions.length} souscription(s) affichée(s) sur {souscriptions.length} total
          </div>
        </CardContent>
      </Card>

      {/* Tableau des souscriptions */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des Souscriptions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">Réf.Solidaire</th>
                  <th className="text-left p-3">Membre adhérent</th>
                  <th className="text-left p-3">Dispositif</th>
                  <th className="text-left p-3">Montant</th>
                  <th className="text-left p-3">Date Début</th>
                  <th className="text-left p-3">Date Fin</th>
                  <th className="text-left p-3">Statut</th>
                </tr>
              </thead>
              <tbody>
                {filteredSouscriptions.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center p-8 text-gray-500">
                      Aucune souscription trouvée
                    </td>
                  </tr>
                ) : (
                  filteredSouscriptions.map((sub) => (
                    <tr key={sub.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-mono text-sm">{sub.numero_police}</td>
                      <td className="p-3">
                        <div className="font-medium">{sub.member?.nom} {sub.member?.prenom}</div>
                        <div className="text-xs text-gray-500">{sub.member?.entreprise}</div>
                      </td>
                      <td className="p-3">
                        <div className="font-medium">{sub.dispositif?.nom}</div>
                        <div className="text-xs text-gray-500">{sub.dispositif?.type}</div>
                      </td>
                      <td className="p-3 font-semibold">{sub.montant_total}€</td>
                      <td className="p-3 text-sm">
                        {new Date(sub.date_debut).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="p-3 text-sm">
                        {new Date(sub.date_fin).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="p-3">
                        {getStatusBadge(sub)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
